import { partition } from 'lodash-es'
import {
  type GenericIssue,
  type GenericSchema,
  type InferInput,
  type InferOutput,
  safeParse,
  safeParseAsync,
} from 'valibot'

const buildErrorPath = (issue: GenericIssue<unknown>): string => {
  return issue.path?.map(p => p.key).join('.') ?? ''
}

export interface IValidationErrorEntry {
  propertyName: string
  message: string
  allowedValues?: string[]
  providedValue?: any
}

const extractMissingPropertyName = (
  issue: GenericIssue<unknown>,
): string | undefined => {
  const received = (issue as { received?: unknown }).received
  const expected = (issue as { expected?: unknown }).expected

  if (received !== 'undefined' && received !== '"undefined"') {
    return undefined
  }

  if (typeof expected === 'string') {
    return expected.replace(/^"|"$/g, '')
  }

  const lastPathEntry = issue.path?.[issue.path.length - 1]
  if (typeof lastPathEntry?.key === 'string') {
    return lastPathEntry.key
  }

  return undefined
}

const extractRequirement = (
  issue: GenericIssue<unknown>,
): string | undefined => {
  const requirement = (issue as { requirement?: unknown }).requirement

  if (!requirement) {
    return undefined
  }

  return String(requirement)
}

const mapIssueMessage = (issue: GenericIssue<unknown>): string => {
  const missingProperty = extractMissingPropertyName(issue)

  if (missingProperty) {
    return `Must have required property '${missingProperty}'`
  }

  const requirement = extractRequirement(issue)
  if (issue.message === 'Invalid value provided' && requirement) {
    return `${issue.message}: Must satisfy ${requirement}`
  }

  return issue.message
}

const mapIssuePropertyName = (
  path: string,
  issue: GenericIssue<unknown>,
): string => {
  const missingProperty = extractMissingPropertyName(issue)

  if (!missingProperty) {
    return path
  }

  if (path === missingProperty) {
    return ''
  }

  const suffix = `.${missingProperty}`
  return path.endsWith(suffix) ? path.slice(0, -suffix.length) : path
}

const errorsFromIssues = (
  output: unknown,
  issues?: GenericIssue[],
): IValidationErrorEntry[] => {
  if (!issues) {
    return []
  }

  return issues.flatMap<IValidationErrorEntry>(baseIssue => {
    const path = buildErrorPath(baseIssue)
    const subIssues = baseIssue.issues ?? [baseIssue]
    const [picklistIssues, remainingIssues] = partition(
      subIssues,
      i => i.type === 'picklist',
    )
    const [typeIssues, otherIssues] = partition(remainingIssues, i =>
      i.message.includes('Invalid type'),
    )

    return [
      ...(picklistIssues.length
        ? [
            {
              propertyName: path,
              message: 'Must be equal to one of the allowed values',
              allowedValues: picklistIssues[0].expected
                ?.replace(/^\(|\)$/g, '')
                .split(' | ')
                .map(v => v.replace(/^"|"$/g, '')),
              providedValue: picklistIssues[0].input,
            },
          ]
        : []),
      ...(typeIssues.length
        ? [
            {
              propertyName: path,
              message: `Must be of type ${typeIssues.map(t => t.expected).join(' or ')}`,
              providedValue: typeIssues[0].input,
            },
          ]
        : []),
      ...otherIssues.map(i => ({
        propertyName: mapIssuePropertyName(path, i),
        message: mapIssueMessage(i),
        providedValue: i.input,
      })),
    ]
  })
}

type SafeParseResult<TSchema extends GenericSchema> = {
  success: boolean
  output: InferOutput<TSchema>
  issues?: GenericIssue[]
}

const buildResult = <TSchema extends GenericSchema>({
  success,
  output,
  issues,
}: SafeParseResult<TSchema>): {
  output: InferOutput<TSchema>
  errors: IValidationErrorEntry[]
} => {
  if (success) {
    return { output, errors: [] }
  }

  return { output, errors: errorsFromIssues(output, issues) }
}

export const validateSync = <
  TSchema extends GenericSchema,
  TData extends Partial<InferInput<TSchema>>,
>(
  schema: TSchema,
  data: { [k in keyof TData]: unknown },
) => buildResult(safeParse(schema, data, { abortEarly: false }))

export const validate = async <TSchema extends GenericSchema>(
  schema: TSchema,
  data: unknown,
) => buildResult(await safeParseAsync(schema, data, { abortEarly: false }))

export type vOutput<T extends GenericSchema> = InferOutput<T>
export type vInput<T extends GenericSchema> = InferInput<T>

export * from './schema/core.ts'
export * from './schema/custom.ts'
